// Phase C — Forwarder-side Carrier Bids panel.
//
// Renders inside LogisticsDetail. Two sections: an aggregated table of
// received bids (sorted best-first) and a "Generate carrier link" button
// + modal. Created links show a copyable public URL; revoke is one-click.
import React, { useState } from "react";
import { Truck, Copy, Trash2, Plus, Send, X, ExternalLink, CheckCircle2 } from "lucide-react";
import {
  useCarrierBids,
  useCarrierLinks,
  useCreateCarrierLink,
  useRevokeCarrierLink,
} from "../api/carrierBidder";
import { toast } from "sonner";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const _publicUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (typeof window !== "undefined") return `${window.location.origin}${path}`;
  return path;
};

const CreateLinkModal = ({ logisticsRequestId, open, onClose }) => {
  const create = useCreateCarrierLink();
  const [form, setForm] = useState({ carrier_name: "", carrier_email: "", ttl_days: 14 });
  const [created, setCreated] = useState(null);

  if (!open) return null;

  const submit = async () => {
    try {
      const r = await create.mutateAsync({
        logistics_request_id: logisticsRequestId,
        carrier_name: form.carrier_name.trim(),
        carrier_email: form.carrier_email || null,
        ttl_days: Number(form.ttl_days) || 14,
      });
      setCreated(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to create link");
    }
  };

  const close = () => { setCreated(null); setForm({ carrier_name: "", carrier_email: "", ttl_days: 14 }); onClose(); };

  return (
    <div
      className="fixed inset-0 z-[600] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={close}
      data-testid="carrier-link-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-md w-full max-w-md"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(243,177,74,0.40)" }}
      >
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15, color: "#f3b14a" }}>
            Generate carrier bid link
          </div>
          <button onClick={close} data-testid="carrier-link-modal-close" style={{ background: "transparent", border: "none", color: "#9aa1b3", cursor: "pointer" }}>
            <X size={16} />
          </button>
        </div>
        {!created ? (
          <div className="p-4 space-y-3">
            <label className="block">
              <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Carrier name *</div>
              <input
                value={form.carrier_name}
                onChange={(e) => setForm({ ...form, carrier_name: e.target.value })}
                style={{ width: "100%", padding: "8px 10px", background: "var(--bg, #0a0c14)", border: "1px solid rgba(255,255,255,0.12)", color: "var(--text, #f0f2f8)", ...mono(12) }}
                placeholder="e.g. Trans Hungária Kft"
                data-testid="carrier-link-carrier-name"
                autoFocus
              />
            </label>
            <label className="block">
              <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Email (optional · MOCKED)</div>
              <input
                type="email"
                value={form.carrier_email}
                onChange={(e) => setForm({ ...form, carrier_email: e.target.value })}
                style={{ width: "100%", padding: "8px 10px", background: "var(--bg, #0a0c14)", border: "1px solid rgba(255,255,255,0.12)", color: "var(--text, #f0f2f8)", ...mono(12) }}
                data-testid="carrier-link-carrier-email"
              />
            </label>
            <label className="block">
              <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Expires in (days)</div>
              <input
                type="number"
                min={1}
                max={60}
                value={form.ttl_days}
                onChange={(e) => setForm({ ...form, ttl_days: e.target.value })}
                style={{ width: "100%", padding: "8px 10px", background: "var(--bg, #0a0c14)", border: "1px solid rgba(255,255,255,0.12)", color: "var(--text, #f0f2f8)", ...mono(12) }}
                data-testid="carrier-link-ttl"
              />
            </label>
          </div>
        ) : (
          <div className="p-4 space-y-3" data-testid="carrier-link-created">
            <div className="flex items-center gap-2" style={{ color: "#4ed084" }}>
              <CheckCircle2 size={16} />
              <div style={mono(12, "#4ed084", 700)}>Link generated</div>
            </div>
            <div style={mono(10, "var(--text-dim, #9aa1b3)")}>Public URL — share with the carrier (Brevo email MOCKED):</div>
            <div
              className="flex items-center gap-2 rounded p-2"
              style={{ background: "var(--bg, #0a0c14)", border: "1px solid rgba(243,177,74,0.40)" }}
            >
              <input
                readOnly
                value={_publicUrl(created.public_url)}
                style={{ flex: 1, background: "transparent", border: "none", color: "#f3b14a", ...mono(11), outline: "none" }}
                data-testid="carrier-link-public-url"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(_publicUrl(created.public_url)); toast.success("Link copied"); }
                  catch (_) { toast.error("Copy failed"); }
                }}
                style={{ background: "rgba(243,177,74,0.20)", color: "#f3b14a", border: "1px solid rgba(243,177,74,0.45)", padding: "4px 8px", borderRadius: 4, ...mono(10, "#f3b14a", 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }}
                data-testid="carrier-link-copy"
              >
                <Copy size={11} style={{ display: "inline", marginRight: 4 }} />Copy
              </button>
            </div>
            <div style={mono(10, "var(--text-dim, #9aa1b3)")}>
              Email preview (would be sent via Brevo):
            </div>
            <pre
              className="rounded p-2 overflow-x-auto"
              style={{ background: "var(--bg, #0a0c14)", border: "1px solid rgba(255,255,255,0.08)", ...mono(10, "#cfd5e3"), whiteSpace: "pre-wrap" }}
              data-testid="carrier-link-email-preview"
            >
              To: {created.email?.to || "(no email provided)"}
              {"\n"}Subject: {created.email?.subject}
              {"\n\n"}{created.email?.body_preview}
            </pre>
          </div>
        )}
        <div className="px-4 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          {!created ? (
            <>
              <button
                onClick={close}
                className="px-3 py-2 rounded"
                style={{ border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "var(--text-dim, #9aa1b3)", ...mono(11, "var(--text-dim, #9aa1b3)", 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={!form.carrier_name.trim() || create.isPending}
                className="px-3 py-2 rounded inline-flex items-center gap-1"
                style={{ background: "#f3b14a", color: "#08090d", border: "none", ...mono(11, "#08090d", 700, "0.8px"), textTransform: "uppercase", opacity: (!form.carrier_name.trim() || create.isPending) ? 0.6 : 1, cursor: "pointer" }}
                data-testid="carrier-link-create-submit"
              >
                <Send size={12} /> {create.isPending ? "Creating…" : "Generate link"}
              </button>
            </>
          ) : (
            <button
              onClick={close}
              className="px-3 py-2 rounded"
              style={{ background: "#4ed084", color: "#08090d", border: "none", ...mono(11, "#08090d", 700, "0.8px"), textTransform: "uppercase", cursor: "pointer" }}
              data-testid="carrier-link-modal-done"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const CarrierBidsPanel = ({ logisticsRequestId }) => {
  const { data: bidsData, isLoading: bidsLoading } = useCarrierBids(logisticsRequestId);
  const { data: linksData } = useCarrierLinks(logisticsRequestId);
  const revoke = useRevokeCarrierLink();
  const [modalOpen, setModalOpen] = useState(false);

  const bids = bidsData?.items || [];
  const links = linksData?.items || [];

  const copy = async (path) => {
    try { await navigator.clipboard.writeText(_publicUrl(path)); toast.success("Link copied"); }
    catch (_) { toast.error("Copy failed"); }
  };

  return (
    <section
      className="rounded-md p-4 mt-4"
      style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid rgba(243,177,74,0.30)" }}
      data-testid="carrier-bids-panel"
    >
      <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Truck size={16} color="#f3b14a" />
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 15 }}>
            Carrier bids ({bids.length})
          </h2>
          {bidsData?.best && (
            <span
              className="ml-2 px-2 py-0.5 rounded inline-flex items-center gap-1"
              style={{ background: "rgba(78,208,132,0.15)", color: "#4ed084", border: "1px solid rgba(78,208,132,0.45)", ...mono(10, "#4ed084", 700, "0.6px"), textTransform: "uppercase" }}
              data-testid="carrier-bids-best"
            >
              Best · {bidsData.best.rate} {bidsData.best.currency}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="px-3 py-1.5 rounded inline-flex items-center gap-1"
          style={{ background: "#f3b14a", color: "#08090d", border: "none", ...mono(10, "#08090d", 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }}
          data-testid="carrier-bids-new-link"
        >
          <Plus size={12} /> New carrier link
        </button>
      </header>

      {bidsLoading && <div style={mono(11, "var(--text-dim, #9aa1b3)")}>Loading…</div>}

      {bids.length > 0 && (
        <div className="rounded mb-4 overflow-x-auto" style={{ border: "1px solid rgba(0,0,0,0.10)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
            <thead>
              <tr style={{ background: "rgba(0,0,0,0.03)" }}>
                {["Carrier", "Rate", "Pickup ETA", "Note", "At"].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", ...mono(9, "var(--text-dim, #6b7388)", 700, "1.2px"), textTransform: "uppercase", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bids.map((b, idx) => (
                <tr key={b.id} data-testid={`carrier-bid-row-${b.id}`} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", background: idx === 0 ? "rgba(78,208,132,0.06)" : "transparent" }}>
                  <td style={{ padding: "8px 10px", ...mono(12) }}>{b.carrier_name}</td>
                  <td style={{ padding: "8px 10px", ...mono(13, undefined, 700) }}>{b.rate} {b.currency}</td>
                  <td style={{ padding: "8px 10px", ...mono(11, "var(--text-dim, #6b7388)") }}>{(b.estimated_pickup_at || "").slice(0, 16).replace("T", " ") || "—"}</td>
                  <td style={{ padding: "8px 10px", ...mono(11, "var(--text-dim, #6b7388)"), maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.note || "—"}</td>
                  <td style={{ padding: "8px 10px", ...mono(10, "var(--text-dim, #6b7388)") }}>{(b.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {links.length > 0 && (
        <details data-testid="carrier-links-details" style={{ ...mono(11) }}>
          <summary style={{ cursor: "pointer", ...mono(10, "var(--text-dim, #6b7388)", 700, "0.8px"), textTransform: "uppercase" }}>
            Issued links ({links.length})
          </summary>
          <div className="mt-2 space-y-1.5">
            {links.map((l) => (
              <div
                key={l.id}
                className="flex items-center gap-2 rounded px-2 py-1.5 flex-wrap"
                style={{ background: l.revoked ? "rgba(120,120,120,0.06)" : "rgba(243,177,74,0.06)", border: `1px solid ${l.revoked ? "rgba(120,120,120,0.30)" : "rgba(243,177,74,0.30)"}` }}
                data-testid={`carrier-link-row-${l.id}`}
              >
                <span style={mono(11, undefined, 600)}>{l.carrier_name}</span>
                <span style={mono(10, "var(--text-dim, #6b7388)")}>
                  {l.revoked ? "REVOKED" : (l.submission_count > 0 ? "USED" : "PENDING")}
                </span>
                <span style={mono(9, "var(--text-dim, #6b7388)")}>exp {(l.expires_at || "").slice(0, 10)}</span>
                {!l.revoked && (
                  <>
                    <button onClick={() => copy(l.public_url)} style={{ background: "transparent", border: "1px solid rgba(243,177,74,0.40)", color: "#f3b14a", padding: "2px 6px", borderRadius: 4, ...mono(9, "#f3b14a", 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }} data-testid={`carrier-link-copy-${l.id}`}>
                      <Copy size={10} style={{ display: "inline", marginRight: 3 }} />Copy
                    </button>
                    <a href={_publicUrl(l.public_url)} target="_blank" rel="noreferrer" style={{ ...mono(9, "#79b5f0", 700, "0.6px"), textTransform: "uppercase", textDecoration: "none", padding: "2px 6px", border: "1px solid rgba(121,181,240,0.40)", borderRadius: 4 }}>
                      <ExternalLink size={10} style={{ display: "inline", marginRight: 3 }} />Open
                    </a>
                    <button
                      onClick={() => { if (window.confirm("Revoke this link?")) revoke.mutate(l.id); }}
                      style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.40)", color: "#ff8a8a", padding: "2px 6px", borderRadius: 4, ...mono(9, "#ff8a8a", 700, "0.6px"), textTransform: "uppercase", cursor: "pointer" }}
                      data-testid={`carrier-link-revoke-${l.id}`}
                    >
                      <Trash2 size={10} style={{ display: "inline", marginRight: 3 }} />Revoke
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {bids.length === 0 && links.length === 0 && (
        <div className="text-center py-6" style={mono(11, "var(--text-dim, #6b7388)")} data-testid="carrier-bids-empty">
          No carrier links yet. Click <strong>New carrier link</strong> to generate one and email it (mocked).
        </div>
      )}

      <CreateLinkModal logisticsRequestId={logisticsRequestId} open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
};

export default CarrierBidsPanel;
